import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildOrderData } from '../../helpers/tableUtil';
import { SortDirection } from '../../models/global';
import { State } from '../../models/state';
import { getAllCountries, getAllRegions } from '../../store/actions/country';
import { getOrganizations } from '../../store/actions/organization';
import Button from '../FormComponents/Button';
import Dropdown from '../FormComponents/Dropdown';
import Table from '../Table/Table';
import ToastMessage from '../ToastMessage/ToastMessage';

const tableMap = new Map<string, string>([
  ['Name', 'name'],
  ['Country', 'country.name'],
  ['Region', 'country.region'],
  ['Language', 'language'],
  ['Parallel Filling (beta)', 'parallelFilling'],
]);
const columns = Array.from(tableMap.keys());

function Organizations() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const rowsPerPage = 10;
  const [page, setPage] = useState(0);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [toast, setToast] = useState('');
  const { organizations, organizationCount } = useSelector((state: State) => state.organization);
  const { regions: regionFilters, countries: countryFilters } = useSelector(
    (state: State) => state.country.simpleFilters,
  );
  const [pageLoading, setPageLoading] = useState(false);
  const [regionFilter, setRegionFilter] = useState<string>('');
  const [countryFilter, setCountryFilter] = useState<string>('');

  const [orderBy, setOrderBy] = useState('Name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    if (!regionFilters.length) {
      dispatch(getAllRegions());
    }
    if (!countryFilters.length) {
      dispatch(getAllCountries());
    }
  }, [dispatch, regionFilters.length, countryFilters.length]);

  useEffect(() => {
    const runOnce = () => {
      setToastType('success');
      setToast(location?.state?.message);
      window.history.replaceState({}, '');
    };
    if (location?.state?.message) {
      runOnce();
    }
  }, [location?.state?.message]);

  useEffect(() => {
    const orderData = buildOrderData({
      orderBy,
      sortDirection,
      tableMap,
    });

    (() => setPageLoading(true))();
    dispatch(
      getOrganizations(
        {
          limit: rowsPerPage,
          page: page + 1,
          orderData,
          regionFilter,
          countryFilter,
        },
        () => setPageLoading(false),
      ),
    );
  }, [dispatch, orderBy, page, sortDirection, regionFilter, countryFilter]);

  const goToCreateOrganization = () => {
    navigate('/organizations/create');
  };

  const getOrganizationsList = () => {
    return organizations.map(organization => {
      return {
        ...organization,
        country: organization.country?.name,
        region: organization.country?.region,
      };
    });
  };

  if (pageLoading) {
    return (
      <Box className='loader'>
        <CircularProgress size={40} color='inherit' />
      </Box>
    );
  }
  return (
    <>
      <Box className='list'>
        <Box className='list-header' sx={{ mb: 2 }}>
          <Typography variant='h5'>Organizations</Typography>
          <Box className='create-button'>
            <Button text='Create' onClick={goToCreateOrganization} />
          </Box>
        </Box>

        <Box className='simple-filters'>
          <Dropdown
            value={regionFilter}
            label='Region'
            options={regionFilters}
            onChange={(_, v: string) => setRegionFilter(v)}
          />
          <Dropdown
            value={countryFilter}
            label='Country'
            options={countryFilters}
            onChange={(_, v: string) => setCountryFilter(v)}
          />
        </Box>

        <Table
          columns={columns}
          rows={getOrganizationsList()}
          count={organizationCount}
          page={page}
          rowsPerPage={rowsPerPage}
          setPage={setPage}
          rowClickHandler={(id: string) => navigate(`/organizations/${id}`)}
          orderBy={orderBy}
          handleOrder={setOrderBy}
          sortDirection={sortDirection}
          handleSortDirection={setSortDirection}
        />
      </Box>
      <ToastMessage toast={toast} close={() => setToast('')} messageType={toastType} />
    </>
  );
}

export default Organizations;
